import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Divider, Stack } from '@esgian/esgianui';
import KPIChangeDisplay from '@components/Display/KPIChangeDisplay/KPIChangeDisplay';
import { BASELINE_TYPE, DISPLAY_UNIT } from '@constants/index';
import { getPercentDiff, getTotSum, roundToSixDecimals } from '@helpers';
import { useDisplayUnit } from '@hooks';
import { useSelector } from 'react-redux';
import { getBaselineDurationHours } from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';
import { getDisplayUnit } from '@store/features';
import {
  getMainDurationDays,
  getMainDurationHours
} from '@store/features/filters/MainPageSlice/MainPageSlice';
import { findSignal } from '@api/helpers';

function PeriodOverviewKpis({ 
  mainData = null,
  baselineData = null,
  loading = false
}) {
  const { valueWithUnit } = useDisplayUnit();
  const displayUnit = useSelector(getDisplayUnit);
  const mainDurationHours = useSelector(getMainDurationHours);
  const mainDurationDays = useSelector(getMainDurationDays);
  const baselineDurationHours = useSelector(getBaselineDurationHours);
  const { getKpiDisplayText } = useDisplayUnit();

  const { mainTotals = null, baselineTotals = null } = useMemo(() => {
    if (!mainData || !baselineData) return {};
    let mainRig = {};
    let baselineRig = {};
    const isTargetBaseline = baselineData.baselineTypeId === BASELINE_TYPE.TARGET;
    Object.values(DISPLAY_UNIT)?.forEach((unit) => {
      let mainDataSignal = findSignal(mainData, 'Rig', unit);
      let baselineDataSignal = findSignal(baselineData, 'Rig', unit);
      if (mainDataSignal) {
        mainRig[unit] = getTotSum(mainDataSignal.timeSeriesValues);
      }

      if (!isTargetBaseline && baselineDataSignal) {
        baselineRig[unit] = getTotSum(baselineDataSignal.timeSeriesValues);
      }
    });
    if (isTargetBaseline) {
      let days = 1; // don't multiply if its total
      if (baselineData.daily) {
        Object.keys(mainRig).forEach((unit) => {
          // We need to apply the same logic for the main data as for the baseline data. Otherwise, we will get a small decimal offset
          let daily = roundToSixDecimals(mainRig[unit] / mainDurationDays);
          mainRig[unit] = roundToSixDecimals(daily * mainDurationDays);
        });

        days = mainDurationDays;
      } else {
        Object.keys(mainRig).forEach((unit) => {
          mainRig[unit] = roundToSixDecimals(mainRig[unit]);
        });
      }
      baselineRig[DISPLAY_UNIT.ENERGY] = baselineData.energyConsumption * days;
      baselineRig[DISPLAY_UNIT.CO2] = baselineData.co2Emissions * days;
      baselineRig[DISPLAY_UNIT.NOX] = baselineData.noxEmissions * days;
      baselineRig[DISPLAY_UNIT.FUEL] = baselineData.fuelConsumption * days;
    }
    return { mainTotals: mainRig, baselineTotals: baselineRig };
  }, [mainData, baselineData]);

  const kpis = useMemo(() => {
    if (!mainTotals || !baselineTotals) return {};
    let baseDuration = baselineDurationHours;
    if (baselineData.baselineTypeId === BASELINE_TYPE.TARGET) {
      baseDuration = mainDurationHours;
    }

    let mainTot = mainTotals[displayUnit];
    let baselineTot = baselineTotals[displayUnit];

    const mainHasData = mainTot != null && mainTot !== 0;
    if (!mainHasData) {
      return {
        total: { value: null, title: null, percent: null },
        dayAvg: { value: null, title: null, percent: null },
        hourAvg: { value: null, title: null, percent: null }
      };
    }

    let mainHour = mainTot / mainDurationHours;
    let baseHour = baselineTot / baseDuration;
    let mainDay = (mainHour * mainDurationHours) / (mainDurationHours / 24);
    let baseDay = (baseHour * baseDuration) / (baseDuration / 24);

    let totDiff = mainTot - baselineTot;
    return {
      total: {
        ...valueWithUnit(totDiff, displayUnit, true),
        percent: getPercentDiff(baselineTot, mainTot)
      },
      dayAvg: {
        ...valueWithUnit(mainDay - baseDay, displayUnit, true),
        percent: getPercentDiff(baseDay, mainDay)
      },
      hourAvg: {
        ...valueWithUnit(mainHour - baseHour, displayUnit, true),
        percent: getPercentDiff(baseHour, mainHour)
      }
    };
  }, [displayUnit, mainTotals, baselineTotals]);

  return (
    <Stack
      direction={'row'}
      justifyContent={'space-evenly'}
      divider={<Divider sx={{ height: 'inherit' }} orientation={'vertical'} />}>
      <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.total?.percent}
        value={kpis.total?.value ?? null}
        unit={kpis.total?.title}
        title={getKpiDisplayText('Total')}
        tooltipText={'The total difference between the selected rig total and the baseline total at the end of the comparison period.'}
        tooltipEnterDelayMs={500}
      />
      <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.dayAvg?.percent}
        value={kpis.dayAvg?.value ?? null}
        unit={kpis.dayAvg?.title}
        title={getKpiDisplayText('Average', 'day')}
        tooltipText={'The difference between average per day within the selected rig time period and the average per day within the baseline time period.'}
        tooltipEnterDelayMs={500}
      />
      <KPIChangeDisplay
        loading={loading}
        percentChange={kpis.hourAvg?.percent}
        value={kpis.hourAvg?.value ?? null}
        unit={kpis.hourAvg?.title}
        title={getKpiDisplayText('Average', 'hour')}
        tooltipText={'The difference between average per hour within the selected rig time period and the average per hour within the baseline time period.'}
        tooltipEnterDelayMs={500}
      />
    </Stack>
  );
}

PeriodOverviewKpis.propTypes = {
  mainData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
      signals: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          rollup: PropTypes.string,
          timeSeriesValues: PropTypes.object, 
          measurementUnit: PropTypes.string,
          metricType: PropTypes.string
        })
      )
    })
  ),
  baselineData: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
        signals: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            rollup: PropTypes.string,
            timeSeriesValues: PropTypes.object, 
            measurementUnit: PropTypes.string,
            metricType: PropTypes.string
          })
        )
      })
    ), 
    PropTypes.object
  ]),
  loading: PropTypes.bool
};

export default PeriodOverviewKpis;
